// =============================================================================
// SimSense Platform — API Client
// =============================================================================
// Fetch wrapper with auth cookie support and error handling.

const BASE_URL = "";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let _refreshing: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (_refreshing) return _refreshing;
  _refreshing = fetch(`${BASE_URL}/api/ss/auth/refresh`, {
    method: "POST",
    credentials: "include",
  }).then(r => r.ok).catch(() => false).finally(() => { _refreshing = null; });
  return _refreshing;
}

/**
 * Typed fetch wrapper for API calls.
 * Automatically includes credentials (cookies) for auth.
 * On 401, attempts a token refresh and retries once.
 */
export async function api<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const fetchOpts: RequestInit = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  };

  let res = await fetch(url, fetchOpts);

  // Auto-refresh on 401 (skip for auth endpoints to avoid loops)
  if (res.status === 401 && !path.startsWith("/api/ss/auth/")) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      res = await fetch(url, fetchOpts);
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let message = body || `HTTP ${res.status}`;
    try { const p = JSON.parse(body); if (p.detail) message = p.detail; } catch { /* not JSON */ }
    throw new ApiError(res.status, message);
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

export async function uploadFormData<T>(
  path: string,
  formData: FormData,
  method = "POST"
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  let res = await fetch(url, {
    method,
    credentials: "include",
    body: formData,
  });

  if (res.status === 401 && !path.startsWith("/api/ss/auth/")) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      res = await fetch(url, {
        method,
        credentials: "include",
        body: formData,
      });
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let message = body || `HTTP ${res.status}`;
    try { const p = JSON.parse(body); if (p.detail) message = p.detail; } catch { /* not JSON */ }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

/**
 * Upload a file via multipart form data.
 */
export async function uploadFile(
  path: string,
  file: File,
  onProgress?: (pct: number) => void
): Promise<{ jobId: string }> {
  const doUpload = (): Promise<{ jobId: string }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BASE_URL}${path}`);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else if (xhr.status === 401) {
          // Try token refresh then retry
          tryRefreshToken().then((ok) => {
            if (!ok) return reject(new ApiError(401, "Unauthorized"));
            const retry = new XMLHttpRequest();
            retry.open("POST", `${BASE_URL}${path}`);
            retry.withCredentials = true;
            retry.upload.onprogress = xhr.upload.onprogress;
            retry.onload = () => {
              if (retry.status >= 200 && retry.status < 300) {
                resolve(JSON.parse(retry.responseText));
              } else {
                reject(new ApiError(retry.status, retry.responseText));
              }
            };
            retry.onerror = () => reject(new Error("Upload failed"));
            const retryForm = new FormData();
            retryForm.append("file", file);
            retry.send(retryForm);
          });
        } else {
          reject(new ApiError(xhr.status, xhr.responseText));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));

      const form = new FormData();
      form.append("file", file);
      xhr.send(form);
    });

  return doUpload();
}

/**
 * Download a file via API.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let message = body || `HTTP ${res.status}`;
    try { const p = JSON.parse(body); if (p.detail) message = p.detail; } catch { /* not JSON */ }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}
